import React, { useState, useEffect } from 'react'
// import PropTypes from 'prop-types'
import RateMap from './rateMap'
import RateDetail from './rateDetail'
import sectionStyles from '../../styles/tracker-modules/ratesSection.module.less'
import { feature } from 'topojson-client'
import { formatTimestamp } from '../sfc/component-helpers/datehelpers'
import useSWR from 'swr'
import { getData } from '../sfc/component-helpers/requesthelpers'
import { dataLabels } from './vaccine-component-helpers/dataLabels'

//Set up settings for individual states.
//The dataLabels object translates short Keys to full labels.
const stateMapSettings = {
  Texas: {
    mapStyle: 'partial', //show percent partially/fully vaccinated detail section style
    maxWidth: 500,
    projectionConfig: {
      rotate: [99, 7.3, 0],
      scale: 4500,
    },
    mapSettings: {
      width: 980,
      height: 880,
    },
    mapKeys: ['ppartv', 'pvf'],
  },
  California: {
    mapStyle: 'per100k', //show doses admin per 100k detail section style
    maxWidth: 500,
    projectionConfig: {
      rotate: [118, 1.2, 0],
      scale: 2200,
    },
    mapSettings: {
      width: 400,
      height: 400,
    },
    mapKeys: ['a/100k'],
  },
  'New York': {
    mapStyle: 'per100k2', // or partial, tbd
    maxWidth: 500,
    projectionConfig: {
      rotate: [75, -4, 0],
      scale: 8000,
    },
    mapSettings: {
      width: 980,
      height: 750,
    },
    mapKeys: ['pct_fv'],
  },
  Connecticut: {
    mapStyle: 'connecticut', // or partial, tbd
    maxWidth: 500,
    projectionConfig: {
      rotate: [72, -2.9, 0],
      scale: 32000,
    },
    mapSettings: {
      width: 980,
      height: 750,
    },
    mapKeys: ['pct_fv'],
  },
  Michigan: {
    mapStyle: 'michigan', // or partial, tbd
    maxWidth: 500,
    projectionConfig: {
      rotate: [85.5, -6, 0],
      scale: 6800,
    },
    mapSettings: {
      width: 980,
      height: 750,
    },
    mapKeys: ['pct_fv'],
  },
  Illinois: {
    mapStyle: 'illinois', // or partial, tbd
    maxWidth: 500,
    projectionConfig: {
      rotate: [89.2, -1, 1],
      scale: 7000,
    },
    mapSettings: {
      width: 980,
      height: 750,
    },
    mapKeys: ['PctVaccinatedPopulation'],
  },
}

function processData(mapGeography, mapData, mapState) {
  //get map geography and create temp object for modification
  // NOTE: MAKE SURE THERE'S A FEATURES PROP TO GRAB
  // Error otherwise is "TypeError: Cannot read property 'length' of undefined"
  let tempData = feature(mapGeography, mapGeography.objects.features).features

  // let tempData = mapGeography.objects.features.geometries

  tempData.forEach((feature) => {
    //match geodata to incoming data
    let matchingVaccData

    if (mapState === 'New York') {
      //match county to its region
      matchingVaccData = mapData.find((d) =>
        d.cs.includes(feature.properties.name)
      )
      matchingVaccData.n = feature.properties.name
    } else if (mapState === 'Connecticut') {
      matchingVaccData = mapData.find((d) => d.t === feature.properties.town)
      matchingVaccData.n = feature.properties.town
      // let coverage = matchingVaccData['first_dose_coverage']
      // matchingVaccData['first_dose_coverage'] = coverage / 100
    } else {
      //match county to FIPS id
      matchingVaccData = mapData.find(
        (d) => d['GEOID'] === feature.properties.fp
      )
    }

    //swap feature properties for new properties from vaccine data
    //NOTE: We will want to adjust if we end up including static data on the original geodata
    feature.properties = { ...matchingVaccData }

    return feature
  })

  tempData.sort((a, b) => a.properties.n.localeCompare(b.properties.n))

  return tempData
}

function RatesSection({ mapURL, mapState, source, marketKey, mapGeography }) {
  // get vaccination data
  const { data: mapData, mapError } = useSWR(mapURL, getData, {
    // onSuccess: (data) => console.log('mapdata', data, mapURL),
    onError: (err) => console.log(err, 'dataerror'),
    revalidateOnFocus: false,
  })

  let [fullMapData, setFullMapData] = useState(null)

  //active area is a feature from the geojson
  let [activeArea, setActiveArea] = useState(null)

  function changeActiveArea(countyName, source) {
    let newActiveArea = fullMapData.find((d) => d.properties.n === countyName)
    setActiveArea(newActiveArea)

    if (typeof window.ens_specialEvent !== 'undefined') {
      window.ens_specialEvent(
        `Vaccine Tracker ${marketKey}`,
        `Rate section county ${source}`,
        `${newActiveArea.properties.n}`
      )
    } else {
      console.log(
        'would send GA event',
        `Vaccine Tracker ${marketKey}`,
        `Rate section county ${source}`,
        `${newActiveArea.properties.n}`
      )
    }
  }

  useEffect(() => {
    if (mapData !== undefined) {
      let processedData = processData(mapGeography, mapData.result, mapState)
      setFullMapData(processedData)
      setActiveArea(processedData[0])
    }
  }, [mapGeography, mapData])

  return (
    <div className={sectionStyles.container}>
      {fullMapData !== null && activeArea !== null && (
        <>
          <div className={sectionStyles.hedContainer}>
            <p className="footnote">
              {mapState} data last updated: {formatTimestamp(mapData.mod_date)}
            </p>
          </div>

          <RateMap
            className={sectionStyles.mapContainer}
            activeArea={activeArea}
            click={changeActiveArea}
            projectionName="geoAlbers"
            mapName={`${mapState} Vaccination Rates`}
            mapData={fullMapData}
            projectionConfig={stateMapSettings[mapState].projectionConfig}
            mapSettings={stateMapSettings[mapState].mapSettings}
            style={{
              maxWidth: `${stateMapSettings[mapState].maxWidth}px`,
              margin: 'auto',
            }}
            mapKeys={stateMapSettings[mapState].mapKeys}
            dataLabels={dataLabels}
            marketKey={marketKey}
          />

          <div className={sectionStyles.selectContainer}>
            {mapState === 'Connecticut' && (
              <label for="county-select" className={sectionStyles.instructions}>
                Choose a town to see details
              </label>
            )}

            {mapState !== 'Connecticut' && (
              <label for="county-select" className={sectionStyles.instructions}>
                Choose a county to see details
              </label>
            )}

            <div className={sectionStyles.select}>
              <select
                name="county-select"
                value={activeArea.properties.n}
                onChange={(e) => changeActiveArea(e.target.value, 'select')}
              >
                {fullMapData.map((d) => (
                  <option value={d.properties.n} key={d.properties.n}>
                    {d.properties.n === mapState
                      ? `${d.properties.n} (all counties)`
                      : d.properties.n}
                  </option>
                ))}
              </select>
              <span className={sectionStyles.focus}></span>
            </div>
          </div>

          <RateDetail
            style={stateMapSettings[mapState].mapStyle}
            className={sectionStyles.detailContainer}
            areaName={activeArea.properties.n}
            areaRegion={activeArea.properties.r}
            population={activeArea.properties['pop']}
            numAdmin={activeArea.properties.a}
            numDistrib={activeArea.properties.d}
            firstDose={activeArea.properties['first_doses']}
            firstDoseCoverage={
              activeArea.properties['first_dose_coverage'] / 100
            }
            adminPer100k={activeArea.properties['a/100k']}
            mapState={mapState}
            pctFullyVaccinated={activeArea.properties.pct_fv * 100}
            numFullyVaccinated={activeArea.properties.num_fv}
            pctPartiallyVaccinated={activeArea.properties.pct_pv * 100}
            numPartiallyVaccinated={activeArea.properties.num_pv}
          />

          <div className={sectionStyles.footer}>
            <p
              className="footnote"
              dangerouslySetInnerHTML={{ __html: source }}
            ></p>
          </div>
        </>
      )}
    </div>
  )
}

// RatesSection.propTypes = {}

export default React.memo(RatesSection)
